import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useAuthSession } from '@/api';
import { useTranslations } from '@/i18n/TranslationsProvider';
import { cn } from '@/lib/cn';
import { AuthHeader } from './AuthHeader';
import { Brand } from './Brand';
import { PRIMARY_NAV } from './nav-config';

export interface TopNavProps {
  className?: string;
  logoUrl?: string | null;
}

/** Desktop header: brand, primary navigation and the auth/balance cluster. */
export function TopNav({ className, logoUrl }: TopNavProps) {
  const { t, i18n } = useTranslation();
  const { selectedLanguage } = useTranslations();
  const { loggedIn } = useAuthSession();
  const translationNamespaceReady =
    selectedLanguage === 'en' || i18n.hasResourceBundle(selectedLanguage, 'translation');
  const shouldHoldTranslatedHeaderText = !translationNamespaceReady;

  return (
    <header
      className={cn(
        'bg-chrome-header sticky top-0 z-30 hidden h-14 items-center gap-6 border-b',
        'border-border-subtle px-6 lg:flex',
        className,
      )}
    >
      <Brand logoUrl={logoUrl} />

      <nav
        aria-label={t('Primary', { defaultValue: 'Primary' })}
        className="flex flex-1 items-center gap-1"
      >
        {PRIMARY_NAV.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            // Only the home link should stop matching as soon as a child route opens.
            activeOptions={{ exact: item.to === '/' }}
            className="rounded-sm px-3.5 py-2.5 text-label-m"
            inactiveProps={{ className: 'text-text-secondary hover:text-text-primary' }}
            activeProps={{ className: 'bg-action-primary font-bold text-text-on-action' }}
          >
            {shouldHoldTranslatedHeaderText ? (
              <span aria-hidden className="opacity-0">
                {item.label}
              </span>
            ) : (
              t(item.translateKey ?? item.label, { defaultValue: item.label })
            )}
          </Link>
        ))}
      </nav>

      <AuthHeader loggedIn={loggedIn} />
    </header>
  );
}
